import {
  Breadcrumbs,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { from, IGrouping } from "manipula";
import React from "react";
import {
  BooleanField,
  CreateButton,
  Datagrid,
  DateField,
  Error,
  FunctionField,
  Link,
  Loading,
  Record,
  ResourceContextProvider,
  sanitizeListRestProps,
  TitleForRecord,
  TopToolbar,
  useGetOne,
  useListContext,
  useTranslate,
  Responsive,
  SimpleList,
} from "react-admin";
import { RouteComponentProps } from "react-router-dom";
import resourceNames from "../../../../common/data/resourceNames";
import { CustomResourceRouteParams } from "../../../../common/types";
import {
  VehicleMaintenanceListDto,
  VehicleMaintenanceSummaryDto,
} from "../types";
import WarningIcon from "@material-ui/icons/Warning";
import "./VehicleMaintenanceList.css";
import { ActionsField } from "../../../common/data-grid-fields/ActionsField";
import { DeleteRecordMenuItem } from "../../../common/data-grid-fields/DeleteRecordMenuItem";
import { TireStatusDto } from "../../../../common/data/service-proxies";
import { ProLogsEmpty } from "../../../common/list/ProLogsEmpty";
import vehicleMaintenance from ".";
import { DefaultProLogsList } from "../../../common/list/DefaultProLogsList";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";

interface Props
  extends Partial<RouteComponentProps<CustomResourceRouteParams>> {}

const useStyles = makeStyles(
  {
    root: {},
    main: {
      display: "flex",
    },
    noActions: {
      marginTop: "1em",
    },
    card: {
      flex: "1 1 auto",
    },
  },
  { name: "RaShow" }
);

const ListActions = (props: any) => {
  const { className, exporter, filters, ...rest } = props;
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {permissions[permissionNames.assets.maintenance.create] ? (
        <CreateButton basePath={basePath} />
      ) : null}
    </TopToolbar>
  );
};

export const VehicleMaintenanceList = (props: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles(props);
  const translate = useTranslate();
  const vehicleId = props.match?.params.id;
  const { loading, error, data } = useGetOne<
    VehicleMaintenanceSummaryDto & Record
  >(resourceNames.ASSETS_MAINTENANCE, vehicleId ?? 0);
  const permissions = useActionPermissions();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  if (!data) {
    return null;
  }

  const Breadcrumb = () => (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to={`/${resourceNames.ASSETS_MAINTENANCE}`}>
        <Typography variant="h6">
          {translate("resources.assets-maintenance.name")}
        </Typography>
      </Link>
      <Typography variant="h6">{data.vehicleName}</Typography>
    </Breadcrumbs>
  );

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Date:
          </Typography>
          <DateField
            source="date"
            label="resources.assets-vehicle-maintenance.labels.date"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Regular oil change:
          </Typography>
          <BooleanField
            source="isRegularMaintenance"
            label="resources.assets-vehicle-maintenance.labels.isRegularMaintenance"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Odometer:
          </Typography>
          <FunctionField<VehicleMaintenanceListDto & Record>
            source="vehicleOdometer"
            label="resources.assets-maintenance.labels.vehicleOdometer"
            render={(record) =>
              record && (
                <Typography variant="body2">
                  {record.vehicleOdometer} {data.vehicleOdometerUnit}
                </Typography>
              )
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Next oil change due date:
          </Typography>
          <DateField
            source="nextMaintenanceDate"
            label="resources.assets-vehicle-maintenance.labels.nextMaintenanceDate"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Next oil change due mileage:
          </Typography>
          <FunctionField<VehicleMaintenanceListDto & Record>
            source="nextMaintenanceOdometer"
            label="resources.assets-vehicle-maintenance.labels.nextMaintenanceOdometer"
            render={(record) =>
              record &&
              record.nextMaintenanceOdometer && (
                <Typography variant="body2">
                  {record.nextMaintenanceOdometer} {data.vehicleOdometerUnit}
                </Typography>
              )
            }
          />
        </div>
      </div>
    );
  };

  let numColumns = 0;
  let axleData: IGrouping<number | null | undefined, TireStatusDto>[] = [];
  if (data.tireStatuses && data.tireStatuses.length) {
    const grouping = from(data.tireStatuses ?? []).groupBy((x) => x.axle);
    numColumns = grouping.max((x) => x.count());
    axleData = grouping.toArray();
  }

  return (
    <div id="vehicle-maintenance-list" className="content-container">
      <TitleForRecord title={<Breadcrumb />} record={data} />
      <Paper id="vehicle-maintenance-view-container" className={classes.card}>
        <div id="vehicle-maintenance-header-container">
          <div className="header-row">
            <div className="vehicle-maintenance-header-info">
              <strong>
                {translate("resources.assets-maintenance.labels.vehicleName")}
              </strong>
              <div>{data.vehicleName}</div>
            </div>
            <div className="vehicle-maintenance-header-info">
              <strong>
                {translate(
                  "resources.assets-maintenance.labels.vehicleOdometer"
                )}
              </strong>
              <div>
                {data.vehicleOdometer} {data.vehicleOdometerUnit}
              </div>
            </div>
          </div>
          <div className="header-row">
            <div className="vehicle-maintenance-header-info">
              <strong>
                {translate("resources.assets-maintenance.labels.vehicleMake")}
              </strong>
              <div>{data.vehicleMake}</div>
            </div>
            <div className="vehicle-maintenance-header-info">
              <strong>
                {translate("resources.assets-maintenance.labels.vehicleYear")}
              </strong>
              <div>{data.vehicleYear}</div>
            </div>
          </div>
          {!numColumns ? null : (
            <div className="header-row">
              <Table>
                <TableHead>
                  <TableRow>
                    <th>
                      {translate("resources.assets-maintenance.labels.axles")}
                    </th>
                    <th colSpan={numColumns}>
                      {translate("resources.assets-maintenance.labels.tires")}
                    </th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {axleData.map((axle) => (
                    <TableRow key={axle.key}>
                      <th>
                        {translate("resources.assets-maintenance.labels.axle")}{" "}
                        {axle.key}
                      </th>
                      {axle.toArray().map((tire) => (
                        <TableCell key={tire.tire}>
                          <div className="tire-info">
                            <div>
                              {tire.tire ? (
                                <>
                                  <div className="tire-info-label">
                                    {translate(
                                      "resources.assets-maintenance.labels.tire"
                                    )}
                                  </div>
                                  <div className="tire-info-value">
                                    {tire.tire}
                                  </div>
                                </>
                              ) : null}
                              {tire.psi ? (
                                <>
                                  <div className="tire-info-label">
                                    {translate(
                                      "resources.assets-maintenance.labels.psi"
                                    )}
                                  </div>
                                  <div className="tire-info-value">
                                    {tire.psi}
                                  </div>
                                </>
                              ) : null}
                              {tire.temperature ? (
                                <>
                                  <div className="tire-info-label">
                                    {translate(
                                      "resources.assets-maintenance.labels.temperature"
                                    )}
                                  </div>
                                  <div className="tire-info-value">
                                    {tire.temperature}
                                  </div>
                                </>
                              ) : null}
                              {tire.airLeakageRate ? (
                                <>
                                  <div className="tire-info-label">
                                    {translate(
                                      "resources.assets-maintenance.labels.airLeakageRate"
                                    )}
                                  </div>
                                  <div className="tire-info-value">
                                    {tire.airLeakageRate}
                                  </div>
                                </>
                              ) : null}
                            </div>
                            {tire.faults && tire.faults.length ? (
                              <div className="tire-faults">
                                <WarningIcon
                                  fontSize="small"
                                  style={{
                                    color: theme.palette.error.main,
                                  }}
                                />
                                <div>
                                  {tire.faults.map((fault, i) => (
                                    <Typography key={i + 1} variant="body2">
                                      {translate(fault, { _: fault })}
                                    </Typography>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Paper>
      <ResourceContextProvider value={resourceNames.ASSETS_VEHICLE_MAINTENANCE}>
        <DefaultProLogsList
          title=" "
          filter={{ vehicleId: data.id }}
          actions={<ListActions />}
          bulkActionButtons={false}
          perPage={10}
          empty={
            <ProLogsEmpty
              iconType={vehicleMaintenance.icon}
              hideCreateButton={
                !permissions[permissionNames.assets.maintenance.create]
              }
            />
          }
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                linkType={false}
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                className="simple-list-wrapper"
                rightIcon={(record) => {
                  return (
                    <>
                      <ActionsField {...props} record={record}>
                        <DeleteRecordMenuItem />
                      </ActionsField>
                    </>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <DateField
                  source="date"
                  label="resources.assets-vehicle-maintenance.labels.date"
                />
                <BooleanField
                  source="isRegularMaintenance"
                  label="resources.assets-vehicle-maintenance.labels.isRegularMaintenance"
                />
                <FunctionField<VehicleMaintenanceListDto & Record>
                  source="vehicleOdometer"
                  label="resources.assets-maintenance.labels.vehicleOdometer"
                  render={(record) =>
                    record && (
                      <Typography variant="body2">
                        {record.vehicleOdometer} {data.vehicleOdometerUnit}
                      </Typography>
                    )
                  }
                />
                <DateField
                  source="nextMaintenanceDate"
                  label="resources.assets-vehicle-maintenance.labels.nextMaintenanceDate"
                />
                <FunctionField<VehicleMaintenanceListDto & Record>
                  source="nextMaintenanceOdometer"
                  label="resources.assets-vehicle-maintenance.labels.nextMaintenanceOdometer"
                  render={(record) =>
                    record &&
                    record.nextMaintenanceOdometer && (
                      <Typography variant="body2">
                        {record.nextMaintenanceOdometer}{" "}
                        {data.vehicleOdometerUnit}
                      </Typography>
                    )
                  }
                />
                <ActionsField>
                  <DeleteRecordMenuItem />
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </ResourceContextProvider>
    </div>
  );
};
